import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';

export default function CopyExerciseToOtherDaysModal({
  isOpen,
  exerciseName,
  periodLabel,
  days = [],
  onConfirm,
  onClose,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds([]);
    setIsSubmitting(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, isSubmitting]);

  if (!isOpen) return null;

  const selectable = days.filter((d) => d.hasOtherPlan);
  const selectableIds = selectable.map((d) => String(d.weekdayId));

  const toggle = (weekdayId) => {
    const id = String(weekdayId);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleConfirm = async () => {
    if (!selectedIds.length || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm(selectedIds);
      onClose();
    } catch {
      // Parent shows error; keep open for retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={() => !isSubmitting && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="copy-exercise-other-days-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div>
            <h3 id="copy-exercise-other-days-title" className="text-lg font-bold text-gray-900">
              Copy to other days
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Copy <span className="font-medium text-gray-700">{exerciseName || 'this exercise'}</span>
              {periodLabel ? ` (${periodLabel})` : ''} to other day plans.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {selectable.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              {days.length === 0
                ? 'No other days in this schedule.'
                : 'No other days with a workout plan to copy to. Assign a workout first.'}
            </p>
          ) : (
            <>
              {selectable.length > 1 && (
                <div className="flex gap-3 text-sm mb-2">
                  <button
                    type="button"
                    onClick={() => setSelectedIds([...selectableIds])}
                    disabled={isSubmitting}
                    className="text-orange-primary hover:underline font-medium disabled:opacity-50"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedIds([])}
                    disabled={isSubmitting}
                    className="text-gray-500 hover:underline disabled:opacity-50"
                  >
                    Clear
                  </button>
                </div>
              )}
              <ul className="space-y-1">
                {days.map((day) => {
                  const id = String(day.weekdayId);
                  const checked = selectedIds.includes(id);
                  const disabled = !day.hasOtherPlan || isSubmitting;
                  return (
                    <li key={id}>
                      <label
                        className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${
                          disabled
                            ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                            : checked
                              ? 'border-orange-primary bg-orange-50'
                              : 'border-gray-200 hover:border-orange-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggle(day.weekdayId)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-gray-900 capitalize">
                            {day.name}
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            {day.hasSamePlan
                              ? 'Already uses this plan'
                              : day.hasOtherPlan
                                ? day.existingTitle || 'Has a workout plan'
                                : 'Rest day — assign a workout first'}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || selectedIds.length === 0}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-orange-primary hover:bg-orange-600 rounded-lg disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSubmitting
              ? 'Copying…'
              : selectedIds.length
                ? `Copy to ${selectedIds.length} day${selectedIds.length === 1 ? '' : 's'}`
                : 'Select days'}
          </button>
        </div>
      </div>
    </div>
  );
}
